// ChatPanelLexical.tsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {createPortal, flushSync} from 'react-dom';
import {useChat} from './use-chat';
import type {
  Part,
  ChatMessage,
  ChatRole,
  ActionEvent,
} from 'src/components/agentic-ui';
import {useVirtualizer} from '@tanstack/react-virtual';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Button, SubTitleSmall, Chip} from '@spaced-out/ui-design-system';
import {
  Editor,
  type MentionUser,
  type UploadResult,
  type EditorHandle,
} from './editor';
import {DefaultMessagePartView} from './default-message-view';
import {MinimizedChatButton} from './minimized-chat-button';
import {classify} from 'src/utils/classify';
import css from './chat-panel.css';


export type {MentionUser, UploadResult} from './editor';
export type PanelMode =
  | 'floating'
  | 'attached'
  | 'fullscreen'
  | 'hidden'
  | 'minimized';
type FlattenedPart = Part & {
  messageId: string;
  messageRole: ChatRole;
  showAssistantAnchor: boolean;
};
export interface ChatPanelProps {
  chatOptions: Parameters<typeof useChat>[0];
  fetchMentionSuggestions?: (query: string) => Promise<MentionUser[]>;
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>;
  headerSlot?: React.ReactNode;
  height?: number;
  placeholder?: string;
  dragVisibleHeaderHeight?: number;
  // Start screen configuration
  renderStartScreen?: (args: {
    publishMessageAction?: (action: ActionEvent) => void;
  }) => React.ReactNode;

  defaultMode?: PanelMode; // uncontrolled default
  lastModePersistKey?: string; // persist last mode key
  defaultOpen?: boolean;
  onModeChange?: (m: PanelMode) => void;

  attachChatTo?: string | HTMLElement; // used when mode='attached'
  attachHeaderTo?: string | HTMLElement; // optional header container when mode='attached'
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  draggable?: boolean; // header-drag in floating mode
  initialPosition?: {x: number; y: number};
  positionPersistKey?: string; // optional persist of position
  messageHistory?: ChatMessage[];
  onReset?: () => unknown;
  panelLeftMargin?: number; // left margin value for side nav positioning
  onClose?: () => unknown;
}

export interface ChatPanelHandle {
  setMode: (m: PanelMode, options?: {persist?: boolean}) => void;
  setOpen: (o: boolean) => void;
  setPosition: (p: {x: number; y: number}) => void;
  // Open the panel (switching out of 'hidden' if needed) and send a message
  openWithMessage: (parts: Part[]) => Promise<void>;
  currentMode?: PanelMode;
  clearMessages: () => void;
}

type FlipOptions = {
  enabled?: boolean;
  durationMs?: number;
  easing?: string;
  respectReducedMotion?: boolean;
};

/* ================= Draggable header (floating mode) ================= */
// eslint-disable-next-line max-params
function useDraggableFloating(
  panelRef: React.RefObject<HTMLDivElement | null>,
  headerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  posState: {
    pos: {x: number; y: number};
    setPos: (p: {x: number; y: number}) => void;
  },
  persist?: {key?: string},
  visibleHeaderHeight: number = 20,
  minX: number = 0,
) {
  const sizeRef = useRef<{w: number; h: number}>({w: 0, h: 0});
  const persistKey = persist?.key;

  // Use refs to avoid re-running effect when position changes during drag
  const posRef = useRef(posState.pos);
  const setPosRef = useRef(posState.setPos);
  // Update refs during render (not in effect) to always have latest values
  posRef.current = posState.pos;
  setPosRef.current = posState.setPos;

  const save = useCallback(
    (p: {x: number; y: number}) => {
      if (!persistKey) {
        return;
      }
      localStorage.setItem(persistKey, JSON.stringify(p));
    },
    [persistKey],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const panel = panelRef.current;
    const header = headerRef.current;
    if (!panel || !header) {
      return;
    }

    const ro = new ResizeObserver(() => {
      const rect = panel.getBoundingClientRect();
      sizeRef.current = {w: rect.width, h: rect.height};
    });
    ro.observe(panel);

    let dragging = false;
    let startX = 0,
      startY = 0;
    let originX = 0,
      originY = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (
        (e.target as HTMLElement).closest(
          'button, a, input, textarea, [contenteditable="true"]',
        )
      ) {
        return;
      }
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      const rect = panel.getBoundingClientRect();
      originX = rect.left;
      originY = rect.top;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      document.body.style.userSelect = 'none';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) {
        return;
      }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const next = clampToViewport(
        {x: originX + dx, y: originY + dy},
        sizeRef.current,
        visibleHeaderHeight,
        minX,
      );
      setPosRef.current(next);
      posRef.current = next;
    };
    const onPointerUp = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      save(posRef.current);
    };
    const onPointerCancel = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      save(posRef.current);
    };

    header.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      header.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      ro.disconnect();
    };
  }, [enabled, panelRef, headerRef, save, visibleHeaderHeight, minX]);
}

function clampToViewport(
  p: {x: number; y: number},
  size: {w: number; h: number},
  visibleHeaderHeight?: number,
  minX: number = 0,
) {
  const vw = window.innerWidth,
    vh = window.innerHeight;
  const maxX = Math.max(minX, vw - size.w);
  // Allow the panel to overflow the bottom so only the header remains visible
  const headerH = Math.max(0, visibleHeaderHeight ?? size.h);
  const maxY = Math.max(0, vh - headerH);
  return {
    x: Math.min(Math.max(minX, p.x), maxX),
    y: Math.min(Math.max(0, p.y), maxY),
  };
}

function useDraggableMinimized(
  panelRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  args: {
    dragPos: {x: number; y: number} | null;
    setDragPos: (p: {x: number; y: number} | null) => void;
    setRestingY: (y: number) => void;
    persistY?: (y: number) => void;
    didDragRef: React.MutableRefObject<boolean>;
  },
  minX: number = 0,
) {
  const sizeRef = useRef<{w: number; h: number}>({w: 0, h: 0});

  // Keep latest setters in refs so pointermove doesn't rebind
  const dragPosRef = useRef(args.dragPos);
  const setDragPosRef = useRef(args.setDragPos);
  const setRestingYRef = useRef(args.setRestingY);
  const persistYRef = useRef(args.persistY);
  const didDragRef = args.didDragRef;

  dragPosRef.current = args.dragPos;
  setDragPosRef.current = args.setDragPos;
  setRestingYRef.current = args.setRestingY;
  persistYRef.current = args.persistY;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const DRAG_THRESHOLD_PX = 5;
    let dragging = false;
    let startY = 0;
    let originY = 0;

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      didDragRef.current = false;
      startY = e.clientY;
      const rect = panel.getBoundingClientRect();
      // Capture current size for viewport clamping during this drag.
      sizeRef.current = {w: rect.width, h: rect.height};
      originY = rect.top;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      document.body.style.userSelect = 'none';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) {
        return;
      }
      const dy = e.clientY - startY;
      if (!didDragRef.current) {
        if (Math.abs(dy) >= DRAG_THRESHOLD_PX) {
          didDragRef.current = true;
        }
      }
      const next = clampToViewport(
        {x: minX, y: originY + dy},
        sizeRef.current,
        undefined,
        minX,
      );
      setDragPosRef.current(next);
      dragPosRef.current = next;
    };
    const onPointerUp = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      const p = dragPosRef.current;
      setDragPosRef.current(null);
      dragPosRef.current = null;

      // Only commit if the user actually dragged (avoid changing Y on click)
      if (p && didDragRef.current) {
        setRestingYRef.current(p.y);
        persistYRef.current?.(p.y);
      }
    };
    const onPointerCancel = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      document.body.style.userSelect = '';
      setDragPosRef.current(null);
      dragPosRef.current = null;
    };

    panel.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      panel.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [enabled, panelRef, didDragRef, minX]);
}

function getPanelStyle({
  imode,
  minimized,
  floatingFullHeight,
  height,
  pos,
  hasCustomPosition,
  panelLeftMargin,
  minimizedY,
  minimizedDragPos,
}: {
  imode: PanelMode;
  minimized: boolean;
  floatingFullHeight: boolean;
  height: number;
  pos: {x: number; y: number};
  hasCustomPosition: boolean;
  panelLeftMargin?: number;
  minimizedY: number | null;
  minimizedDragPos: {x: number; y: number} | null;
}): React.CSSProperties | undefined {
  const minimizedZIndex = 2000;
  const isFloating = imode === 'floating';
  const isFullscreen = imode === 'fullscreen';
  const dockLeft = panelLeftMargin ?? 0;

  let left: number | undefined;
  if (isFloating || minimized) {
    if (hasCustomPosition) {
      left = pos.x; // user dragged, use their position
    } else if (panelLeftMargin !== undefined) {
      left = panelLeftMargin; // use provided side nav margin
    }
  }

  if (minimized) {
    // While dragging: keep dock X fixed and only move vertically.
    if (minimizedDragPos) {
      return {
        left: dockLeft,
        top: minimizedDragPos.y,
        bottom: 'auto',
        zIndex: minimizedZIndex,
      } as React.CSSProperties;
    }
    // Resting minimized position: keep dock X, but apply stored Y (top) if present.
    if (minimizedY !== null) {
      return {
        left: dockLeft,
        top: minimizedY,
        bottom: 'auto',
        zIndex: minimizedZIndex,
      } as React.CSSProperties;
    }
    return {
      left: dockLeft,
      bottom: 8,
      zIndex: minimizedZIndex,
    } as React.CSSProperties;
  }
  return {
    minHeight: isFullscreen
      ? '100%'
      : isFloating && floatingFullHeight
      ? 'calc(100vh - 70px)'
      : height,
    maxHeight: isFullscreen
      ? '100%'
      : isFloating && floatingFullHeight
      ? 'calc(100vh - 70px)'
      : height,
    width: isFloating ? 420 : '100%',
    position: isFloating ? ('fixed' as const) : ('relative' as const),
    left,
    top: isFloating && hasCustomPosition ? pos.y : undefined,
    zIndex: imode !== 'attached' ? 60 : 'auto',
  } as React.CSSProperties;
}

/* ================= Main Panel ================= */
export const ChatPanelLexical = forwardRef<ChatPanelHandle, ChatPanelProps>(
  (
    {
      chatOptions,
      fetchMentionSuggestions,
      onUploadFiles,
      height = 660,
      placeholder,
      dragVisibleHeaderHeight = 20,
      defaultMode = 'hidden',
      onModeChange,
      attachChatTo,
      attachHeaderTo,
      headerSlot,
      defaultOpen = true,
      onOpenChange,
      draggable = true,
      initialPosition = {x: 16, y: 16},
      positionPersistKey = 'chatPos',
      messageHistory = [],
      renderStartScreen,
      onReset,
      onClose,
      lastModePersistKey = 'chatMode',
      panelLeftMargin,
    },
    ref,
  ) => {
    const {t} = useLabelTranslation();
    const chat = useChat(chatOptions);
    const [minimizedDragPos, setMinimizedDragPos] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const minimizedDidDragRef = useRef(false);
    // uncontrolled mode with persistence
    const [imode, setIMode] = useState<PanelMode>(() => {
      // 1) persisted value (localStorage only)
      if (lastModePersistKey) {
        try {
          const v = (localStorage.getItem(lastModePersistKey) ||
            '') as PanelMode;
          if (
            v === 'floating' ||
            v === 'attached' ||
            v === 'fullscreen' ||
            v === 'hidden'
          ) {
            return v;
          }
        } catch {}
      }
      // 2) fallback to provided default, defaulting to hidden
      return defaultMode;
    });

    // persist mode on change (localStorage only)
    // useEffect(() => {
    //   if (!lastModePersistKey) {
    //     return;
    //   }
    //   try {
    //     localStorage.setItem(lastModePersistKey, imode);
    //   } catch {}
    // }, [imode, lastModePersistKey]);

    const setModeSafe = useCallback(
      (m: PanelMode, options?: {persist?: boolean}) => {
        const shouldPersist = options?.persist ?? true;
        setIMode(m);
        // Persist mode unless explicitly skipped (used by URL-forced fullscreen).
        if (shouldPersist) {
          try {
            localStorage.setItem(lastModePersistKey, m);
          } catch {
            console.error('Failed to persist mode to local storage');
          }
        }
        onModeChange?.(m);
      },
      [onModeChange, lastModePersistKey],
    );

    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
    const minimized = !isOpen;
    const editorRef = useRef<EditorHandle | null>(null);

    // list + scroll
    const scrollerRef = useRef<HTMLDivElement | null>(null);

    // One DOM row per flattened part
    const rowRefs = useRef(new Map<string, HTMLDivElement | null>());
    const rows: FlattenedPart[] = useMemo(() => {
      const historyRows = messageHistory.flatMap((msg) => {
        return msg.parts.map((part, index) => ({
          ...part,
          messageId: msg.id,
          messageRole: msg.role,
          showAssistantAnchor: msg.role === 'assistant',
        }));
      });

      const historyRowsIdSet = new Set(historyRows.map((row) => row.messageId));

      const liveRows = chat.snapshot.messages
        .flatMap(
          (msg) => {
            return msg.parts.map((part, index) => ({
              ...part,
              messageId: msg.id,
              messageRole: msg.role,
              showAssistantAnchor: msg.role === 'assistant',
            }));
          },
          //NOTE:(diwakersurya)sometimes the history api may be hit when the message is
          // there in the localStore. it leads to duplicate messages in the chat panel.
          // we need to make sure that if history api has given a message, we remove it from
          // the local store.
        )
        .filter((row) => !historyRowsIdSet.has(row.messageId));
      return [...historyRows, ...liveRows];
    }, [messageHistory, chat.snapshot.messages]);

    const virtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => scrollerRef.current,
      estimateSize: () => 120,
      overscan: 8,
    });

    const [autoScroll, setAutoScroll] = useState(true);
    const autoScrollRef = useRef(true);
    autoScrollRef.current = autoScroll;

    const onScroll = useCallback<React.UIEventHandler<HTMLDivElement>>((e) => {
      const el = e.currentTarget;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;

      const NEAR_BOTTOM = 40;
      const shouldStick = distanceFromBottom < NEAR_BOTTOM;

      if (shouldStick !== autoScrollRef.current) {
        autoScrollRef.current = shouldStick;
        setAutoScroll(shouldStick);
      }
    }, []);

    // scroll to last part when:
    // - rows grow (new parts/messages)
    // - measurement changes (dynamic height)

    useLayoutEffect(() => {
      if (!autoScrollRef.current || rows.length === 0) {
        return;
      }

      virtualizer.scrollToIndex(rows.length - 1, {align: 'end'});
    }, [rows.length, virtualizer]);

    // Callback for widgets to notify when they resize
    // Widgets call this after their own timing logic (animations/transitions)
    // We just measure the full row height immediately
    const handleWidgetResize = useCallback(
      (rowKey: string) => {
        const rowEl = rowRefs.current.get(rowKey);
        if (!rowEl) {
          return;
        }

        requestAnimationFrame(() => {
          // Re-measure this row because the widget changed size
          virtualizer.measureElement(rowEl);
          virtualizer.measure();

          // If user is "stuck to bottom", keep view pinned to last part
          if (autoScrollRef.current && rows.length > 0) {
            virtualizer.scrollToIndex(rows.length - 1, {align: 'end'});
          }
        });
      },
      [virtualizer, rows.length],
    );

    const onSendUserMessage = useCallback(
      async (parts: Part[]) => {
        autoScrollRef.current = true;
        setAutoScroll(true);
        await chat.sendMessage(parts);
      },
      [chat],
    );

    // header + drag
    const panelRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    // Lazy initialization: read persisted position or use initialPosition
    const [pos, setPos] = useState<{x: number; y: number}>(() => {
      if (!positionPersistKey) {
        return initialPosition;
      }
      const read = () => {
        const v = localStorage.getItem(positionPersistKey);
        if (!v) {
          return null;
        }
        try {
          return JSON.parse(v) as {x: number; y: number};
        } catch {
          return null;
        }
      };
      return read() || initialPosition;
    });

    const hasCustomPositionRef = useRef(
      positionPersistKey ? !!localStorage.getItem(positionPersistKey) : false,
    );

    // Minimized button Y (persisted separately; X always snaps to dock X)
    const minimizedYStorageKey = positionPersistKey
      ? `${positionPersistKey}:minimizedY`
      : undefined;
    const [minimizedY, setMinimizedY] = useState<number | null>(() => {
      if (!minimizedYStorageKey) {
        return null;
      }
      try {
        const v = localStorage.getItem(minimizedYStorageKey);
        if (!v) {
          return null;
        }
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : null;
      } catch {
        return null;
      }
    });
    const saveMinimizedY = useCallback(
      (y: number) => {
        if (!minimizedYStorageKey) {
          return;
        }
        try {
          localStorage.setItem(minimizedYStorageKey, String(y));
        } catch {}
      },
      [minimizedYStorageKey],
    );

    // track when user drags to set custom position
    const handleSetPos = useCallback((newPos: {x: number; y: number}) => {
      hasCustomPositionRef.current = true;
      setPos(newPos);
    }, []);

    const [floatingFullHeight, _setFloatingFullHeight] =
      useState<boolean>(true);
    useDraggableFloating(
      panelRef,
      headerRef,
      draggable && imode === 'floating' && isOpen,
      {pos, setPos: handleSetPos},
      //NOTE:(diwakersurya)we are not persisting the position of the chat panel in the local storage for now.
      //{key: positionPersistKey},
      undefined,
      dragVisibleHeaderHeight,
      panelLeftMargin ?? 0,
    );

    // Initialize minimizedY from current docked location to avoid a jump when we first
    // start using `top` instead of CSS `bottom`.
    useLayoutEffect(() => {
      if (!minimized || minimizedY !== null) {
        return;
      }
      const panelEl = panelRef.current;
      if (!panelEl) {
        return;
      }
      const rect = panelEl.getBoundingClientRect();
      // Prefer using the current visual top so switching to `top` doesn't move it.
      if (Number.isFinite(rect.top)) {
        setMinimizedY(rect.top);
      }
    }, [minimized, minimizedY]);

    // Dragging the minimized button (free drag while dragging, snap X on release)
    useDraggableMinimized(
      panelRef,
      minimized,
      {
        dragPos: minimizedDragPos,
        setDragPos: setMinimizedDragPos,
        setRestingY: setMinimizedY,
        persistY: saveMinimizedY,
        didDragRef: minimizedDidDragRef,
      },
      panelLeftMargin ?? 0,
    );

    // container for attached mode - compute during render instead of effect
    const container =
      imode === 'attached'
        ? attachChatTo instanceof HTMLElement
          ? attachChatTo
          : typeof attachChatTo === 'string'
          ? (document.querySelector(attachChatTo) as HTMLElement | null)
          : null
        : null;

    const headerContainer =
      imode === 'attached'
        ? attachHeaderTo instanceof HTMLElement
          ? attachHeaderTo
          : typeof attachHeaderTo === 'string'
          ? (document.querySelector(attachHeaderTo) as HTMLElement | null)
          : null
        : null;

    // expose imperative API
    useImperativeHandle(
      ref,
      () => ({
        setMode: setModeSafe,
        setOpen: (o: boolean) =>
          onOpenChange ? onOpenChange(o) : setIsOpen(o),
        setPosition: setPos,
        clearMessages: () => {
          chat.clear();
        },
        openWithMessage: async (parts: Part[]) => {
          if (!isOpen) {
            if (onOpenChange) {
              onOpenChange(true);
            } else {
              setIsOpen(true);
            }
          }

          await chat.sendMessage(parts);
          // If hidden, reveal as floating by default
          if (imode === 'hidden') {
            setModeSafe('floating');
          }
        },

        currentMode: imode,
      }),
      [setModeSafe, onOpenChange, imode, isOpen, chat],
    );

    // Hidden mode: render nothing at all (no button)
    if (imode === 'hidden') {
      return null;
    }

    const shouldShowCloseButton = isOpen && imode === 'fullscreen';

    const startContent = renderStartScreen?.({
      publishMessageAction: chatOptions.publishMessageAction,
    });
    const handleReset = () => {
      hasCustomPositionRef.current = false;
      chat.clear();
      onReset?.();
    };
    const showStartScreen =
      startContent !== null && chat.snapshot.messages.length === 0;
    const isOnInitialPage = showStartScreen;

    const headerContent = (
      <>
        <div className={css.headerNameAndBackButtonContainer}>
          {!isOnInitialPage && (
            <Button
              type="ghost"
              iconLeftName="chevron-left"
              size="small"
              onClick={handleReset}
            ></Button>
          )}

          <SubTitleSmall>{t('Sense_IQ', 'Sense IQ')}</SubTitleSmall>
          <Chip size="small">{t('Beta', 'Beta')}</Chip>
          {imode === 'attached' ? headerSlot : null}
        </div>
        <div className={css.headerControls}>
          {/* TODO: implement history functionality then uncomment this */}
          {/* <Button type="ghost" iconLeftName="history" size="small"></Button> */}
          {imode !== 'fullscreen' ? (
            <Button
              type="ghost"
              iconLeftName="minus"
              size="small"
              onClick={() => setIsOpen(false)}
              aria-label={t('Minimize', 'Minimize')}
            ></Button>
          ) : null}
          {/* TODO: implement new fullscreen toggle functionality then uncomment this */}
          {/* {shouldShowFullHeightButton && (
            <Button
              type="ghost"
              size="small"
              iconLeftName={heightToggleButtonIconName}
              onClick={() => setFloatingFullHeight((v) => !v)}
              aria-label={t('Height_toggle', 'Height toggle')}
            ></Button>
          )} */}
          {/* <Button
          type="ghost"
          iconLeftName="map-pin"
          onClick={() => setModeSafe('attached')}
          aria-label={t('Attach', 'Attach')}
        ></Button> */}
          {/* <Button
          type="ghost"
          iconLeftName="paper-plane"
          onClick={() => setModeSafe('floating')}
          aria-label={t('Float', 'Float')}
        ></Button> */}
          {/* <Button
          type="ghost"
          iconLeftName="maximize"
          onClick={() => setModeSafe('fullscreen')}
          aria-label={t('Full', 'Full')}
        ></Button> */}

          {shouldShowCloseButton ? (
            <Button
              type="ghost"
              size="small"
              iconLeftName="close"
              onClick={() => {
                setIsOpen(false);
                onClose?.();
              }}
              aria-label={t('Close', 'Close')}
            ></Button>
          ) : null}
        </div>
      </>
    );

    const header = (
      <header
        ref={headerRef}
        className={classify(css.header, {
          [css.headerNoDrag]: minimized || imode !== 'floating',
        })}
      >
        {headerContent}
      </header>
    );

    const body = (
      <>
        <div ref={scrollerRef} className={css.scroller} onScroll={onScroll}>
          {showStartScreen ? (
            <div className={css.startScreenContainer}>{startContent}</div>
          ) : (
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((item) => {
                const part = rows[item.index];
                if (!part) {
                  return null;
                }

                const rowKey = `${part.messageId}:${part.id ?? item.index}`;

                return (
                  <div
                    data-index={item.index}
                    key={rowKey}
                    ref={(el) => {
                      // keep ref map in sync
                      if (!el) {
                        rowRefs.current.delete(rowKey);
                      } else {
                        rowRefs.current.set(rowKey, el);
                        virtualizer.measureElement(el);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${item.start}px)`,
                    }}
                  >
                    <DefaultMessagePartView
                      part={part}
                      role={part.messageRole}
                      publishMessageAction={chatOptions.publishMessageAction}
                      // if widgets need it later:
                      onWidgetResize={() => handleWidgetResize(rowKey)}
                      showChatBubble={true}
                      showAssistantAnchor={part.showAssistantAnchor}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <footer className={css.footer}>
          <Editor
            ref={editorRef}
            placeholder={placeholder}
            fetchMentionSuggestions={fetchMentionSuggestions}
            onUploadFiles={onUploadFiles}
            onSend={onSendUserMessage}
            disableSend={chat.snapshot.connectionStatus === 'OPEN'}
            autoFocus={isOpen}
          />
        </footer>
      </>
    );

    const isFloatingOpen = !minimized && imode === 'floating';

    const panel = (
      <div
        ref={panelRef}
        className={classify(css.panel, {
          [css.panelMinimized]: minimized,
          [css.panelMinimizedDragging]: minimized && !!minimizedDragPos,
          [css.panelFloating]: isFloatingOpen,
          [css.panelFullscreen]: imode === 'fullscreen',
        })}
        style={getPanelStyle({
          imode,
          minimized,
          floatingFullHeight,
          height,
          pos,
          hasCustomPosition: hasCustomPositionRef.current,
          panelLeftMargin,
          minimizedY,
          minimizedDragPos,
        })}
      >
        {minimized ? (
          <MinimizedChatButton
            variant="primary"
            tooltipTitle={t('Sense_IQ', 'Sense IQ')}
            onClick={() => {
              if (minimizedDidDragRef.current) {
                minimizedDidDragRef.current = false;
                return;
              }
              const rect = panelRef.current?.getBoundingClientRect();
              const x =
                rect && Number.isFinite(rect.left)
                  ? rect.left
                  : typeof panelLeftMargin === 'number'
                  ? panelLeftMargin
                  : null;
              if (x !== null) {
                setPos((p) => ({...p, x}));
              }
              setIsOpen(true);
            }}
            ariaLabel={t('Open', 'Open')}
          />
        ) : imode === 'fullscreen' ? null : (
          header
        )}

        {isOpen ? body : null}
      </div>
    );

    if (minimized) {
      return createPortal(panel, document.body);
    }
    if (imode === 'fullscreen') {
      return createPortal(
        <div className={css.fullscreenShell}>
          <header
            className={classify(
              css.header,
              css.headerNoDrag,
              css.fullscreenHeader,
            )}
          >
            {headerContent}
          </header>
          <div className={css.fullscreenBody}>
            <section className={css.fullscreenAskAiPane}>{panel}</section>
            <section className={css.fullscreenGenericPane}>
              <div
                className={css.fullscreenPortalTarget}
                {...{
                  ['data-askai-fullscreen-portal-target']:
                    'askai-fullscreen-portal',
                }}
              ></div>
            </section>
          </div>
        </div>,
        document.body,
      );
    }
    if (imode === 'floating') {
      return createPortal(panel, document.body);
    }
    if (imode === 'attached' && container) {
      // NOTE:(diwaker) In attached mode, allow host to provide separate containers for header and body
      return (
        <>
          {headerContainer
            ? createPortal(headerContent, headerContainer)
            : null}
          {container ? createPortal(body, container) : null}
        </>
      );
    }
    return panel;
  },
);
